import events from 'lib/events';
import React from 'react';
import round from 'lodash/round';
import session from 'services/sessionService';
import {ScanningErrors, getErrorCodeText} from 'services/errorCodes';
import {scanTypes, chemicalBaseTypes, xfName, xfcExactValue, xfcUntestedValue, xfcBromineSanitizerValue, xfcFreeChlorineId, xfcGreaterThan, xfcLessThan} from './constants';
import {FormattedMessage} from 'react-intl';

// NOTE: this causes a problem for running a test to overwrite existing analyses (mostly on source water page)
// since C# returns null for not set values and Math.min(null, 5) === 0 but Math.min(undefined, 5) === NaN...
// This was causing problems on the iron and copper fields only so using it there since not sure whether
// other parts rely on this behaviour
function minimum(x, y) {
  if (!x && x !== 0 || !y && y === 0) {
    return undefined;
  }
  return Math.min(x, y);
}

function maximum(x, y) {
  if (!x && x !== 0 || !y && y === 0) {
    return undefined;
  }
  return Math.max(x, y);
}


export const canTest = () => {
  if (!session.getUser().privileges.canWaterTest) {
    events.publish('error.user', {message: <FormattedMessage id="water.test.not.permitted" />});
    return false;
  }
  return true;
};

export const discTypesNames = {
  halogen: {
    diskIV: 'Halogen (Cl/Br) Disk 4 26743BIO',
  },
  biguanide: {
    diskIII: 'Biguanide Disk 3 26744BIO',
  },
};

export const discTypesCookiesKeys = {
  halogenDisk: 'halogenDisk',
  biguanideDisk: 'biguanideDisk',
};

export const discTypes = (chemicalBaseType = 2) => {
  if ([
    chemicalBaseTypes.chlorinePool,
    chemicalBaseTypes.mineralSpringsPool,
    chemicalBaseTypes.brominePool,
    chemicalBaseTypes.chlorinePool,
    chemicalBaseTypes.chlorineSpa,
    chemicalBaseTypes.bromineSpa,
    chemicalBaseTypes.mineralSpringsPool,
    chemicalBaseTypes.saltWaterPool,
    chemicalBaseTypes.saltWaterSpa,
    chemicalBaseTypes.synergyPool,
    chemicalBaseTypes.synergySpa,
  ].indexOf(chemicalBaseType) !== -1) {
    return [
      discTypesNames.halogen.diskIV,
    ];
  }

  if ([
    chemicalBaseTypes.softswimPool,
    chemicalBaseTypes.softSoakSpa,
  ].indexOf(chemicalBaseType) !== -1) {
    return [
      discTypesNames.biguanide.diskIII,
    ];
  }

  return [
    'Callibration Check Disk',
  ];
};

export const xfcAnalyteValueMap = {
  PO4: '100',
  TH: '30',
  Fe: '0',
  Cu: '0',
  Salt: '500',
  TA: '20'
}

const disableWarningFields = {
  'iron': true,
  'salt': true,
  'copper':true,
  'alkalinity': true,
  'hardness': true,
  'phosphate': true,
}

export const fromScannerFormat = {
  pH: 'pH',

  // Accuscan Fields
  BOR: 'optimizerPlusBorate',
  BROM: 'totalBromine',
  CYA: 'cYA',
  DD: 'dD',
  FC: 'freeChlorine',
  HD: 'hardness',
  TA: 'alkalinity',
  TC: 'totalChlorine',
  BIG: ['sSWB', 'sSKSanitizerResidual'],
  PEX: ['sSWC', 'sSKShockResidual'],
  CU: 'copper',
  FE: 'iron',

  // SmartLab Fields
  'Free Cl': 'freeChlorine',
  'Total Cl': 'totalChlorine',
  Alkalinity: 'alkalinity',
  Borate: 'optimizerPlusBorate',
  Copper: 'copper',
  Hardness: 'hardness',
  Iron: 'iron',
  Stabilizer: 'cYA',
  Salt: 'salt',
  Bromine: 'totalBromine',
  Biguanide: ['sSWB', 'sSKSanitizerResidual'],
  'Biguanide Shock': ['sSWC', 'sSKShockResidual'],
  Phosphate: 'phosphate',

  // Typo in new lamotte library
  Alkalynity: 'alkalinity',

  // AccuFlex™ Fields
  Fe: 'iron',
  Salt: 'salt',
  Bo: 'optimizerPlusBorate',
  Cu: 'copper',
  FC: 'freeChlorine',
  TC: 'totalChlorine',
  pH: 'pH',
  TA: 'alkalinity',
  TH: 'hardness',
  CYA: 'cYA',
  PO4: 'phosphate',
};

export const scanDisabledFields = {
  Accuscan: {
    Halogen: [
      'alkalinity',
      'cYA',
      'dD',
      'freeChlorine',
      'hardness',
      'optimizerPlusBorate',
      'initiatorLevel',
      'pH',
      'totalBromine',
      'totalChlorine',
    ],
    Biguanide: [
      'alkalinity',
      'cYA',
      'dD',
      'freeChlorine',
      'hardness',
      'optimizerPlusBorate',
      'initiatorLevel',
      'pH',
      'sSKSanitizerResidual',
      'sSKShockResidual',
      'sSWB',
      'sSWC',
      'totalBromine',
      'totalChlorine',
    ],
    Copper: [
      'copper',
    ],
    Iron: [
      'iron',
    ],
  },
  SmartLab: [
    // For backwards compatability.
    'alkalinity',
    'copper',
    'cYA',
    'salt',
    'minerals',
    'freeChlorine',
    'hardness',
    'iron',
    'optimizerPlusBorate',
    'initiatorLevel',
    'pH',
    'sSKSanitizerResidual',
    'sSKShockResidual',
    'sSWB',
    'sSWC',
    'stabilizer',
    'totalBromine',
    'totalChlorine',
  ],
  SmartLab2p0: {},
  XpressFlex: [
    'iron',
    'salt',
    'optimizerPlusBorate',
    'copper',
    'freeChlorine',
    'totalChlorine',
    'totalBromine',
    'pH',
    'alkalinity',
    'hardness',
    'cYA',
    'phosphate',
  ],
};

scanDisabledFields.SmartLab2p0[discTypesNames.biguanide.diskIII] = scanDisabledFields.SmartLab;

let diskIV = scanDisabledFields.SmartLab.concat(['phosphate']);

diskIV.splice(diskIV.indexOf('salt'), 1);
diskIV.splice(diskIV.indexOf('minerals'), 1);
scanDisabledFields.SmartLab2p0[discTypesNames.halogen.diskIV] = diskIV;


export const calibration = {
  chlorine: [
    {
      name: 'general.free.chlorine',
      field: 'Free Cl',
    },
    {
      name: 'general.total.chlorine',
      field: 'Total Cl',
    },
    {
      name: 'general.total.alkalinity',
      field: 'Alkalinity',
    },
    {
      name: 'general.ph',
      field: 'pH',
    },
    {
      name: 'general.calcium.hardness',
      field: 'Hardness',
    },
    {
      name: 'general.copper',
      field: 'Copper',
    },
    {
      name: 'general.iron',
      field: 'Iron',
    },
    {
      name: 'general.cyanuric.acid',
      field: 'Stabilizer',
    },
    {
      name: 'general.borate',
      field: 'Borate',
    },
  ],
};

export const populateFieldsByChemicalBaseType = {
  1: ['totalBromine', 'cYA', 'dD', 'hardness', 'alkalinity', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  2: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  3: ['dD', 'hardness', 'alkalinity', 'sSWB', 'sSWC', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  5: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  6: ['totalBromine', 'cYA', 'dD', 'hardness', 'alkalinity', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  7: ['cYA', 'minerals', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  8: ['dD', 'hardness', 'alkalinity', 'sSKSanitizerResidual', 'sSKShockResidual', 'copper', 'iron', 'pH', 'phosphate'],
  12: ['cYA', 'salt', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  13: ['cYA', 'salt', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  14: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
  15: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate', 'phosphate'],
};


/**
 * Pre-process AccuFlex™ return data before main data-process function
 * This is required to match the return format that the other scanners provide
 * @param {Object} data Return object from AccuFlex™ scan
 * @param {boolean} showError Boolean to determine if error events should be published or not
 * @return {Array<Object>} Returns a 2-element array of the processed data and any invalid analyte fields
 */
const preprocessXFData = (data, showError) => {
  const processedData = {};
  const warningFieldIds = {};
  const scanResults = data;

  if (!scanResults.data.length || scanResults.errorCode !== 0) {
    let errorMessage;

    if (scanResults.errorCode === 300) {
      errorMessage = getErrorCodeText(ScanningErrors.XFC_INVALID_STRIPS);
    }
    errorMessage = errorMessage || getErrorCodeText(ScanningErrors.XFC_UNEXPECTED_ERROR);

    if (scanResults.errorCode && scanResults.errorMsg) {
      const scanError = processXFCErrorMessage(scanResults.errorCode, scanResults.errorMsg, true);
      errorMessage = <React.Fragment>{errorMessage}{'\n\n'}{scanError}</React.Fragment>;
    }

    if (showError) {
      events.publish('error.user', {message: errorMessage});
      console.error(scanResults);
    }

    return [processedData, warningFieldIds];
  }

  for (const chemicalResult of scanResults.data) {
    let analyteValueString = chemicalResult.valueAsString;

    // If analyte value is 'exact' or 'untested', it is a valid response value and not considered a warning
    if (chemicalResult.valueStatus !== xfcExactValue && chemicalResult.valueStatus !== xfcUntestedValue) {
      const scannerFormatAnalyte = fromScannerFormat[chemicalResult.analyteId];

      const isLowLimitAnalyte = analyteValueString && analyteValueString !== '' && analyteValueString.includes(xfcLessThan);

      if (scannerFormatAnalyte && !(disableWarningFields[scannerFormatAnalyte] && isLowLimitAnalyte)) {
        warningFieldIds[scannerFormatAnalyte] = true;
      }
    }

    // Only set the analyte value from XF if it returns a proper value
    if (analyteValueString && analyteValueString !== '') {
      
      // Map low analyte values for certain chemicals
      if (xfcAnalyteValueMap[chemicalResult.analyteId] && analyteValueString.includes(xfcLessThan)) {
        analyteValueString = xfcAnalyteValueMap[chemicalResult.analyteId];
      }

      // If high or low limits, remove the > or < symbol and trim for white-spaces
      analyteValueString = analyteValueString.replace(xfcGreaterThan, '').replace(xfcLessThan, '').trim();

      processedData[chemicalResult.analyteId] = Number(analyteValueString) || 0; // 0 is for any unaccounted cases
    }
  }

  return [processedData, warningFieldIds];
};

  /**
   * Processes the XFC error results to set up an appropriate error message
   * with the error code and error message from copy
   * @param {Number} errorCode Error Code value from results
   * @param {string} [errorMessage='Unknown Error'] Error message
   * @param {boolean} [isScanError=false] Checks if scan error or not (i.e., analyte error)
   * @return {React.Fragment} Processed Error message
   */
const processXFCErrorMessage = (errorCode, errorMessage = 'Unknown Error', isScanError = false) => {
  let processedErrorMsg = errorMessage;
  if (errorCode) {
    const errorID = `xfc.error.${errorCode}`;
    // Fallback to default error message
    const formattedMessage = <FormattedMessage id={errorID} defaultMessage={errorMessage} />

    if (!isScanError) { 
      return formattedMessage;
    }
    const errorHeader = `Scan Error[${errorCode}]`;
    processedErrorMsg = <React.Fragment>{errorHeader}{' - '}{'"'}{formattedMessage}{'"'}</React.Fragment>;
  }
  return processedErrorMsg;
};


/**
 * Generates an error message with a generic header message and body list of analyte field errors
 *
 * @param {Object} results XF scan results
 * @return {React.Fragment} React Fragment with error header and body
 */
const processRetestCodesText = (results) => {
  let errorMessage = '';
  const analyteMessages = [];
  const retestMessages = {};

  if (results.retestCodes && results.retestCodes.length) {
    for (const retestCode of results.retestCodes) {
      if (retestCode.code && retestCode.text) {
        retestMessages[retestCode.code] = processXFCErrorMessage(retestCode.code, retestCode.text);
      }
    }
  }

  for (const analyte of results.data) {
    if (analyte.retestCode !== 0) {
      const analyteErrorHeader = `• ${analyte.analyteName}: Error[${analyte.retestCode || '???'}]`;
      const analyteError = <React.Fragment>{analyteErrorHeader}{' - '}{'"'}{retestMessages[analyte.retestCode]}{'"'}</React.Fragment>;
      analyteMessages.push(analyteError);
    }
  }
  const errorHeader = getErrorCodeText(ScanningErrors.XFC_INVALID_FIELDS);
  // Join all analyte error messages with new lines
  const errorBody = analyteMessages.reduce((allErrors, currError) => <React.Fragment>{allErrors}{'\n'}{currError}</React.Fragment>);

  errorMessage = <React.Fragment>{errorHeader}{'\n\n'}{errorBody}</React.Fragment>;
  return errorMessage;
};

export const preprocessData = (data, analysis, selectedScanner, chemicalBaseType, selectedDisk = undefined, showError = false) => {
  // Handles some of the data for only the AccuFlex™ scanner
  if (selectedScanner === xfName) {
    let xfWarningFieldIds;
    let xfErrorMessage;

    // Case for Bromine system water scan
    if (chemicalBaseType === chemicalBaseTypes.brominePool || chemicalBaseType === chemicalBaseTypes.bromineSpa) {
      const fcIndex = data.data.findIndex((analyte) => analyte.analyteId === xfcFreeChlorineId);
      if (fcIndex !== -1) {
        const freeChlorineAnalyte = data.data[fcIndex];
        if (freeChlorineAnalyte.analyteId) freeChlorineAnalyte.analyteId = xfcBromineSanitizerValue; // Free Chlorine is read in as Bromine
        if (freeChlorineAnalyte.analyteName) freeChlorineAnalyte.analyteName = xfcBromineSanitizerValue;
      }
    }

    const xfcResults = {...data};
    [data, xfWarningFieldIds] = preprocessXFData(data, showError);

    //convert salt warning to minerals warning
    if (chemicalBaseType === 7 && xfWarningFieldIds['salt']) {
      xfWarningFieldIds['mineral'] = true;
    }

    if (Object.keys(xfWarningFieldIds).length > 0 && showError) {
      if (xfcResults.retestCodes && xfcResults.retestCodes.length) {
        xfErrorMessage = processRetestCodesText(xfcResults);
        console.warn(xfcResults.retestCodes, xfcResults.data);
      } else {
        console.warn(xfcResults.data);
      }

      events.publish('error.scannerFieldWarning', xfWarningFieldIds);
      events.publish('error.user', {message: xfErrorMessage || getErrorCodeText(ScanningErrors.XFC_INVALID_FIELDS)});
    }
  }

  // eslint-disable-next-line guard-for-in
  for (let key in data) {
    let value = fromScannerFormat[key];

    //convert salt to minerals
    if (chemicalBaseType === 7 && value === 'salt') {
      value = 'minerals';
      data[value] = data['salt'];
    }

    if (!populateFieldsByChemicalBaseType[chemicalBaseType]) {
      continue;
    }

    if (value instanceof Array && populateFieldsByChemicalBaseType[chemicalBaseType] &&
        (populateFieldsByChemicalBaseType[chemicalBaseType].includes(value[0]) || populateFieldsByChemicalBaseType[chemicalBaseType].includes(value[1]))) {
      analysis[value[0]] = round(Math.max(data[key], 0));
      analysis[value[1]] = round(Math.max(data[key], 0));
    } else if (populateFieldsByChemicalBaseType[chemicalBaseType].includes(value)) {
      if([chemicalBaseTypes.synergyPool, chemicalBaseTypes.synergySpa].includes(chemicalBaseType) && value === 'optimizerPlusBorate') {
        //fill initiator level instead of optimizerPlusBorate for Synergy systems
        analysis.initiatorLevel = round(Math.max(data[key], 0));
      }else{
        analysis[value] = Math.max(data[key], 0);
      // Andie: "pH, Total and Free Chlorine, Total Bromine, Copper and Iron should all be one decimal place.
      // Everything else should be an integer value."
        if (['pH', 'freeChlorine', 'totalChlorine', 'totalBromine', 'copper', 'iron'].indexOf(value) > -1) {
          analysis[value] = round(analysis[value], 1);
        } else {
          analysis[value] = round(analysis[value]);
        }
      }
    }
  }

  if (selectedScanner.indexOf('Smart') >= 0) {

    const DeviceLimit = (min, max) => {
      return {min: min, max: max};
    };
    const fieldToDeviceLimit = {
      alkalinity: DeviceLimit(null, 250),
      copper: DeviceLimit(null, 3),
      iron: DeviceLimit(null, 3),
      cYA: DeviceLimit(null, 150), //stabilizer
      freeChlorine: DeviceLimit(null, 15),
      totalChlorine: DeviceLimit(null, 15),
      pH: DeviceLimit(6.4, 8.6),
      phosphate: DeviceLimit(null, 4000),
      hardness: DeviceLimit(null, 800),
      optimizerPlusBorate: DeviceLimit(null, 80),
      sSWB: DeviceLimit(null, 70), //Biguanide
      sSKSanitizerResidual: DeviceLimit(null, 70), //Biguanide
      sSWC: DeviceLimit(null, 250), // Peroxide
      sSKShockResidual: DeviceLimit(null, 250), // Peroxide
      totalBromine: DeviceLimit(null, 33),
    };

    let warningFieldIds = {};

    Object.keys(fieldToDeviceLimit).forEach((field) => {
      let deviceLimit = fieldToDeviceLimit[field];

      if (deviceLimit.max !== null && analysis[field] > deviceLimit.max) {
        analysis[field] = deviceLimit.max;
      }else if (deviceLimit.min !== null && analysis[field] < deviceLimit.min) {
        analysis[field] = deviceLimit.min;
      }else{
        return;
      }
      warningFieldIds[field] = true;
    });

    events.publish('error.scannerFieldWarning', warningFieldIds);
    if(showError && Object.keys(warningFieldIds).length > 0) {
      let errorMessage = <FormattedMessage id='error.scanner.fieldoutoflimit'/>;

      events.publish('error.user', {message: errorMessage});
    }

    if (selectedDisk) {
      analysis.diskType = selectedDisk;
    }

  }

  analysis.scanTypeID = scanTypes[selectedScanner];

  return analysis;
};
